import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const filterDateTimeForBusinessHours = (
  datetimes: string[],
  timezone: string
): string[] => {
  const BUSINESS_START_HOUR = 8; // 8AM inclusive
  const BUSINESS_END_HOUR = 20; // 8PM inclusive

  return datetimes.filter((dateTime) => {
    const timeInTargetZone = dayjs(dateTime).tz(timezone);
    const hour = timeInTargetZone.hour();

    return hour >= BUSINESS_START_HOUR && hour < BUSINESS_END_HOUR;
  });
};

const sortOutsideBusinessHoursByProximity = (
  datetimes: string[],
  timezone: string
): string[] => {
  const BUSINESS_START_HOUR = 8; // 8AM
  const BUSINESS_END_HOUR = 20; // 8PM

  const getDistanceFromBusinessHours = (
    dateTime: string,
    tz: string
  ): number => {
    const timeInTZ = dayjs(dateTime).tz(tz);
    const hour = timeInTZ.hour();

    if (hour < BUSINESS_START_HOUR) {
      return BUSINESS_START_HOUR - hour;
    } else if (hour >= BUSINESS_END_HOUR) {
      return hour - BUSINESS_END_HOUR + 1;
    } else {
      // Inside business hours (though this shouldn't happen given the function's intent)
      return 0;
    }
  };

  return datetimes.sort((a, b) => {
    const distanceA = getDistanceFromBusinessHours(a, timezone);
    const distanceB = getDistanceFromBusinessHours(b, timezone);

    return distanceA - distanceB;
  });
};

type DateTimeGroup = string[][];

const groupDatesByLocalDays = (
  dates: string[],
  time_zone: string
): DateTimeGroup => {
  const date_map: { [date: string]: string[] } = {};

  dates.forEach((date) => {
    const local_day = dayjs.utc(date).tz(time_zone).format("YYYY-MM-DD");
    if (!date_map[local_day]) {
      date_map[local_day] = [];
    }
    date_map[local_day].push(date);
  });

  return Object.values(date_map);
};

const filterDatesMoreThan48HoursAhead = (datetimes: string[]): string[] => {
  const now = dayjs();
  const threshold = now.add(48, "hour");

  return datetimes.filter((dateTime) => {
    const targetDateTime = dayjs(dateTime);
    return targetDateTime.isAfter(threshold);
  });
};

const reccomendedAvailability = (
  mentor_availability: string[],
  mentee_timezone: string
): DateTimeGroup => {

  const atLeast48HoursAhead =
    filterDatesMoreThan48HoursAhead(mentor_availability);

  const filteredDates = filterDateTimeForBusinessHours(
    atLeast48HoursAhead,
    mentee_timezone
  );

  // Check if there are any dates left in the next 7 days
  const today = dayjs().tz(mentee_timezone).startOf("day");
  const sevenDaysFromToday = today.add(7, "day");
  const datesInNext7Days = filteredDates.filter((date) => {
    const localDate = dayjs.utc(date).tz(mentee_timezone);
    return localDate.isAfter(today) && localDate.isBefore(sevenDaysFromToday);
  });

  if (datesInNext7Days.length === 0) {
    // If no dates are left, sort the original dates by proximity to business hours
    return groupDatesByLocalDays(atLeast48HoursAhead, mentee_timezone);
  }
  return groupDatesByLocalDays(filteredDates, mentee_timezone);
};

export default reccomendedAvailability;
export {
  sortOutsideBusinessHoursByProximity,
  filterDateTimeForBusinessHours,
  groupDatesByLocalDays,
};
